/**
 * Sistema de permissões do GreenVoice.
 *
 * Modelo: bitfield de 64 bits (BigInt) com resolução allow/deny em camadas —
 * o mesmo desenho que o Stoat e o Discord usam, porque é o único que aguenta
 * "esta role pode partilhar ecrã em TODO o lado excepto no canal #direcção"
 * sem uma tabela de junção por cada par (utilizador, canal).
 *
 * Ordem de resolução (a última camada ganha sobre a anterior):
 *   1. @everyone (role base do servidor)
 *   2. roles do membro, aplicadas por rank ascendente (rank 0 = mais alta)
 *   3. overrides do canal para @everyone
 *   4. overrides do canal para cada role do membro
 *   5. override do canal para o membro em concreto
 *   6. o dono do servidor e ADMINISTRATOR ganham sempre tudo (curto-circuito)
 *
 * Dentro de cada camada: deny é aplicado antes de allow (allow ganha o empate),
 * que é a convenção que os utilizadores vindos do Discord esperam.
 */
export const Permission = {
    // ── Servidor ──────────────────────────────────────────────
    /** Bypass total. Trata-o como root: só para a role de admin. */
    ADMINISTRATOR: 1n << 0n,
    MANAGE_SERVER: 1n << 1n,
    MANAGE_CHANNELS: 1n << 2n,
    MANAGE_ROLES: 1n << 3n,
    MANAGE_PERMISSIONS: 1n << 4n,
    MANAGE_EMOJIS: 1n << 5n,
    VIEW_AUDIT_LOG: 1n << 6n,
    // ── Membros ───────────────────────────────────────────────
    KICK_MEMBERS: 1n << 10n,
    BAN_MEMBERS: 1n << 11n,
    TIMEOUT_MEMBERS: 1n << 12n,
    ASSIGN_ROLES: 1n << 13n,
    CHANGE_NICKNAME: 1n << 14n,
    MANAGE_NICKNAMES: 1n << 15n,
    // ── Canais de texto ───────────────────────────────────────
    VIEW_CHANNEL: 1n << 20n,
    READ_MESSAGE_HISTORY: 1n << 21n,
    SEND_MESSAGES: 1n << 22n,
    MANAGE_MESSAGES: 1n << 23n,
    ATTACH_FILES: 1n << 24n,
    EMBED_LINKS: 1n << 25n,
    ADD_REACTIONS: 1n << 26n,
    MENTION_EVERYONE: 1n << 27n,
    CREATE_INVITES: 1n << 28n,
    MANAGE_WEBHOOKS: 1n << 29n,
    CREATE_THREADS: 1n << 30n,
    MANAGE_THREADS: 1n << 31n,
    // ── Voz / vídeo / ecrã ────────────────────────────────────
    // A razão de existir deste projecto vive nestes cinco bits.
    CONNECT: 1n << 40n,
    SPEAK: 1n << 41n,
    VIDEO: 1n << 42n,
    /** Partilha de ecrã. Separado de VIDEO de propósito: há equipas que
     *  querem câmara off e ecrã on (e o inverso em salas de recrutamento). */
    SHARE_SCREEN: 1n << 43n,
    PRIORITY_SPEAKER: 1n << 44n,
    MUTE_MEMBERS: 1n << 45n,
    DEAFEN_MEMBERS: 1n << 46n,
    MOVE_MEMBERS: 1n << 47n,
    /** Gravar a chamada. Nunca no preset default — é uma decisão legal, não técnica. */
    RECORD_SESSION: 1n << 48n,
};
export const PERMISSION_NAMES = Object.keys(Permission);
/** Todos os bits definidos. Não uses `-1n`: partiria à primeira permissão nova. */
export const ALL_PERMISSIONS = Object.values(Permission).reduce((acc, bit) => acc | bit, 0n);
export const NO_PERMISSIONS = 0n;
/**
 * Preset @everyone: o que um colaborador acabado de entrar consegue fazer.
 * Deliberadamente inclui SHARE_SCREEN — é o ponto do produto.
 * Não inclui MENTION_EVERYONE nem CREATE_INVITES (empresa, não servidor público).
 */
export const DEFAULT_MEMBER_PERMISSIONS = Permission.VIEW_CHANNEL |
    Permission.READ_MESSAGE_HISTORY |
    Permission.SEND_MESSAGES |
    Permission.ATTACH_FILES |
    Permission.EMBED_LINKS |
    Permission.ADD_REACTIONS |
    Permission.CHANGE_NICKNAME |
    Permission.CREATE_THREADS |
    Permission.CONNECT |
    Permission.SPEAK |
    Permission.VIDEO |
    Permission.SHARE_SCREEN;
/** Preset de moderador: tudo menos o que mexe na estrutura do servidor. */
export const DEFAULT_MODERATOR_PERMISSIONS = DEFAULT_MEMBER_PERMISSIONS |
    Permission.MANAGE_MESSAGES |
    Permission.MANAGE_THREADS |
    Permission.KICK_MEMBERS |
    Permission.TIMEOUT_MEMBERS |
    Permission.MANAGE_NICKNAMES |
    Permission.MENTION_EVERYONE |
    Permission.CREATE_INVITES |
    Permission.MUTE_MEMBERS |
    Permission.DEAFEN_MEMBERS |
    Permission.MOVE_MEMBERS |
    Permission.VIEW_AUDIT_LOG;
/**
 * Rank da role @everyone. É o fundo da hierarquia: rank 0 = mais alta, e este é
 * o maior inteiro de 32 bits com sinal, portanto nenhuma role criada pode ficar
 * abaixo dela. Vive aqui — e não no serviço — porque o seed e o cliente também
 * precisam de saber onde é o fundo sem arrastar o Nest atrás.
 */
export const EVERYONE_RANK = 2_147_483_647;
export const EMPTY_OVERWRITE = { allow: 0n, deny: 0n };
/**
 * Aplica uma camada: primeiro limpa o que ela nega, depois liga o que permite.
 * allow ganha o empate dentro da mesma camada.
 */
export function applyOverwrite(base, { allow, deny }) {
    return (base & ~deny) | allow;
}
/** Junta N overwrites da MESMA camada (ex.: todas as roles do membro no canal). */
export function mergeOverwrites(overwrites) {
    let allow = 0n;
    let deny = 0n;
    for (const o of overwrites) {
        allow |= o.allow;
        deny |= o.deny;
    }
    // Um allow explícito numa role anula o deny de outra role do mesmo nível.
    return { allow, deny: deny & ~allow };
}
const TIMEOUT_ALLOWED = Permission.VIEW_CHANNEL | Permission.READ_MESSAGE_HISTORY | Permission.CONNECT;
/**
 * Resolve as permissões efectivas de um membro, opcionalmente dentro de um canal.
 * Função pura: a mesma implementação corre no servidor (autorização real) e no
 * cliente (esconder botões). Uma única fonte de verdade evita a classe de bug
 * "o botão aparece mas a API devolve 403".
 */
export function resolvePermissions(ctx) {
    if (ctx.isOwner)
        return ALL_PERMISSIONS;
    let permissions = ctx.everyonePermissions;
    const sorted = [...ctx.roles].sort((a, b) => b.rank - a.rank || (a.id < b.id ? -1 : 1));
    for (const role of sorted) {
        permissions |= role.permissions;
    }
    if (permissions & Permission.ADMINISTRATOR)
        return ALL_PERMISSIONS;
    const ow = ctx.channelOverwrites;
    if (ow) {
        if (ow.everyone)
            permissions = applyOverwrite(permissions, ow.everyone);
        if (ow.roles?.size) {
            const applicable = [];
            for (const role of sorted) {
                const o = ow.roles.get(role.id);
                if (o)
                    applicable.push(o);
            }
            if (applicable.length) {
                permissions = applyOverwrite(permissions, mergeOverwrites(applicable));
            }
        }
        if (ow.member)
            permissions = applyOverwrite(permissions, ow.member);
    }
    if (ctx.isTimedOut)
        permissions &= TIMEOUT_ALLOWED;
    return permissions;
}
export function has(permissions, required) {
    if (permissions & Permission.ADMINISTRATOR)
        return true;
    return (permissions & required) === required;
}
export function hasAny(permissions, required) {
    if (permissions & Permission.ADMINISTRATOR)
        return true;
    return (permissions & required) !== 0n;
}
/** Bits → nomes. Para a UI de definições e para o audit log. */
export function toNames(permissions) {
    return PERMISSION_NAMES.filter((name) => (permissions & Permission[name]) !== 0n);
}
export function fromNames(names) {
    return names.reduce((acc, name) => acc | Permission[name], 0n);
}
/**
 * Serialização: BigInt não sobrevive a JSON.stringify. Trocamos sempre
 * permissões como string decimal, nas duas direcções.
 */
export function serialize(permissions) {
    return permissions.toString(10);
}
export function deserialize(value) {
    if (value === null || value === undefined || value === "")
        return 0n;
    try {
        return BigInt(value);
    }
    catch {
        return 0n;
    }
}
/**
 * Guarda de hierarquia: só podes mexer em quem está abaixo de ti.
 * Sem isto, um moderador expulsa o dono — o bug clássico de todo o clone.
 */
export function canActOn(actor, target) {
    if (target.isOwner)
        return false;
    if (actor.isOwner)
        return true;
    return actor.highestRank < target.highestRank;
}
/** Rank mais alto (número mais baixo) de um conjunto de roles. */
export function highestRank(roles) {
    return roles.reduce((min, r) => (r.rank < min ? r.rank : min), Number.MAX_SAFE_INTEGER);
}
//# sourceMappingURL=permissions.js.map